import type {
  AddonSuggestion,
  AddonSuggestionType,
  VehicleApplicabilityException,
  VehicleContext,
} from '@/types/addonSuggestions';

/**
 * CORE-BP item 2 — make and model participate in the decision.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS, AND WHAT IT REFUSES TO BE
 * ═══════════════════════════════════════════════════════════════════════════
 * Slice A audited every candidate source in this repository and found none that
 * could state truthfully whether a make or model makes a service technically
 * required: `vehicle_catalog` is empty and shaped for engine packages,
 * `training_artifact_fitment` is the wrong domain, `shop_history_vehicles`
 * records what a shop has SEEN. Inventing the answer from general automotive
 * knowledge is the one thing this capability must never do, because a
 * suggestion must state a technical reason that is TRUE and a fabricated
 * reason is worse than silence.
 *
 * So this claims no such authority. It is SHOP-AUTHORED policy, and it can only
 * ever SUPPRESS:
 *
 *     existing technical evidence says "this may apply"
 *   + the shop says "not on this exact make/model"
 *   = do not ask
 *
 * The constitution's own decision flow already provides for exactly this —
 * "vehicle attributes → REFINE where the obligation is vehicle-dependent" —
 * and names shop-tunable knowledge as an input. Refining is what this does.
 * Manufacturing a suggestion from vehicle identity alone is what it cannot do,
 * by construction: it is a filter over suggestions that already exist, and it
 * has no way to add one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE MATCH IS EXACT, AND WHY UNKNOWN MEANS "STILL ASK"
 * ═══════════════════════════════════════════════════════════════════════════
 * A "Corolla" rule must not silence a "Corolla Cross", which is a different
 * vehicle with different front-end geometry. Substring matching would do
 * exactly that, and an alias table would be this module guessing on the shop's
 * behalf about vehicles it has never been told about. So: case-insensitive,
 * whitespace-normalised, otherwise exact. Nothing else.
 *
 * And when the ticket carries no make or model, the suggestion STANDS. Not
 * knowing which vehicle it is cannot be evidence that an obligation does not
 * apply — silence would be the engine inventing a fact from an absence. The
 * failure mode is deliberately asymmetric: an unnecessary question costs a
 * click, a withheld one can cost a comeback.
 */

/** Case-insensitive, whitespace-collapsed. The only normalisation there is. */
export function normalizeVehicleToken(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}

/** A stored exception with usable make and model. Anything else is ignored. */
function isUsable(e: VehicleApplicabilityException | null | undefined): boolean {
  return !!e && !!e.type
    && normalizeVehicleToken(e.make).length > 0
    && normalizeVehicleToken(e.model).length > 0;
}

/**
 * Has the shop said "do not ask about this family on this exact vehicle"?
 *
 * Both make AND model must match. A make-only match is not enough — a shop that
 * excluded one Toyota has said nothing about the others.
 */
export function isFamilyExcludedForVehicle(
  type: AddonSuggestionType,
  vehicle: Pick<VehicleContext, 'make' | 'model'> | null | undefined,
  exceptions: readonly VehicleApplicabilityException[] | null | undefined,
): boolean {
  if (!exceptions || exceptions.length === 0) return false;

  const make = normalizeVehicleToken(vehicle?.make);
  const model = normalizeVehicleToken(vehicle?.model);
  // Fail open. An unidentified vehicle silences nothing.
  if (make.length === 0 || model.length === 0) return false;

  return exceptions.some((e) =>
    isUsable(e)
    && e.type === type
    && normalizeVehicleToken(e.make) === make
    && normalizeVehicleToken(e.model) === model);
}

/**
 * Apply the shop's vehicle policy to suggestions that were already raised.
 *
 * Deliberately a filter over a finished list: whatever evidence produced a
 * suggestion — always-prompt, a catalogue `followup` relationship, or keywords
 * — it is refined the same way, and nothing here can put a suggestion INTO the
 * list. Slice A's relationship semantics and precision-first behaviour are
 * untouched; they decide what is raised, and this decides only what the shop
 * has asked not to be asked about.
 */
export function filterSuggestionsByVehicleApplicability(
  suggestions: readonly AddonSuggestion[],
  vehicle: Pick<VehicleContext, 'make' | 'model'> | null | undefined,
  exceptions: readonly VehicleApplicabilityException[] | null | undefined,
): AddonSuggestion[] {
  if (!exceptions || exceptions.length === 0) return [...suggestions];
  return suggestions.filter((s) => !isFamilyExcludedForVehicle(s.type, vehicle, exceptions));
}

/** Stored JSON → the typed policy. Anything malformed is dropped, not guessed. */
export function parseVehicleExceptions(raw: unknown): VehicleApplicabilityException[] {
  if (!Array.isArray(raw)) return [];
  const valid: AddonSuggestionType[] = ['alignment', 'sas', 'adas', 'tpms'];
  return raw
    .map((r) => (r && typeof r === 'object' ? r as Record<string, unknown> : null))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      type: String(r.type ?? '') as AddonSuggestionType,
      make: String(r.make ?? '').trim(),
      model: String(r.model ?? '').trim(),
    }))
    .filter((e) => valid.includes(e.type) && isUsable(e));
}

/** De-duplicated by the same normalisation the matcher uses. */
export function addVehicleException(
  existing: readonly VehicleApplicabilityException[],
  candidate: VehicleApplicabilityException,
): VehicleApplicabilityException[] {
  if (!isUsable(candidate)) return [...existing];
  const already = existing.some((e) =>
    e.type === candidate.type
    && normalizeVehicleToken(e.make) === normalizeVehicleToken(candidate.make)
    && normalizeVehicleToken(e.model) === normalizeVehicleToken(candidate.model));
  return already
    ? [...existing]
    : [...existing, {
        type: candidate.type,
        make: candidate.make.trim(),
        model: candidate.model.trim(),
      }];
}

export function removeVehicleExceptionAt(
  existing: readonly VehicleApplicabilityException[],
  index: number,
): VehicleApplicabilityException[] {
  return existing.filter((_, i) => i !== index);
}
